import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil, Package, Minus, PlusCircle, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const emptyVariant = () => ({ id: null, sku: "", price: 0, stock: 0, track_inventory: true, low_stock_threshold: "", images: [], options: {} });
const emptyForm = () => ({ name: "", description: "", category: "", images: [], active: true, option_types: [], variants: [emptyVariant()], occasions: [], image: "", imageBack: "", modelImage: "", modelImageBack: "", isNew: false, onSale: false });

export default function InventoryManager({ sub, isAdmin }) {
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const url = isAdmin ? `/products?subaccount_id=${sub.id}` : "/products";
      const { data } = await api.get(url);
      setProducts(data);
    } catch {
      /* ignore */
    }
  }, [sub.id, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      description: p.description || "",
      category: p.category || "",
      images: p.images || [],
      active: p.active,
      option_types: p.option_types || [],
      occasions: p.occasions || [],
      image: p.image || "",
      imageBack: p.imageBack || "",
      modelImage: p.modelImage || "",
      modelImageBack: p.modelImageBack || "",
      isNew: !!p.isNew,
      onSale: !!p.onSale,
      variants: (p.variants || []).map((v) => ({
        id: v.id, sku: v.sku || "", price: v.price || 0, stock: v.stock || 0,
        track_inventory: v.track_inventory, low_stock_threshold: v.low_stock_threshold ?? "",
        images: v.images || [], options: v.options || {},
      })),
    });
    setDialogOpen(true);
  };

  const setOptionTypes = (raw) => {
    const types = raw.split(",").map((t) => t.trim()).filter(Boolean);
    setForm((f) => ({
      ...f,
      option_types: types,
      variants: f.variants.map((v) => {
        const options = {};
        types.forEach((t) => { options[t] = v.options[t] || ""; });
        return { ...v, options };
      }),
    }));
  };

  const updateVariant = (i, patch) => setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) }));
  const setVariantOption = (i, type, val) => setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => (idx === i ? { ...v, options: { ...v.options, [type]: val } } : v)) }));
  const addVariant = () => setForm((f) => { const nv = emptyVariant(); f.option_types.forEach((t) => { nv.options[t] = ""; }); return { ...f, variants: [...f.variants, nv] }; });
  const removeVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        category: form.category,
        images: form.images,
        active: form.active,
        option_types: form.option_types,
        occasions: form.occasions,
        image: form.image || null,
        imageBack: form.imageBack || null,
        modelImage: form.modelImage || null,
        modelImageBack: form.modelImageBack || null,
        isNew: form.isNew,
        onSale: form.onSale,
        variants: form.variants.map((v) => ({
          id: v.id || undefined,
          sku: v.sku || null,
          price: parseFloat(v.price) || 0,
          stock: parseInt(v.stock, 10) || 0,
          track_inventory: v.track_inventory,
          low_stock_threshold: v.low_stock_threshold === "" ? null : parseInt(v.low_stock_threshold, 10),
          images: v.images,
          options: v.options,
        })),
      };
      if (isAdmin) payload.subaccount_id = sub.id;
      if (editingId) await api.put(`/products/${editingId}`, payload);
      else await api.post("/products", payload);
      toast.success(editingId ? "Product updated" : "Product created");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const quickAdjust = async (p, v, delta) => {
    try {
      await api.post(`/products/${p.id}/variants/${v.id}/stock`, { mode: "adjust", quantity: delta });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const variantLabel = (v) => Object.values(v.options || {}).filter(Boolean).join(" / ") || "Default";

  return (
    <div className="space-y-4" data-testid="inventory-manager">
      <div className="flex items-center justify-between">
        <p className="font-mono text-xs text-muted-foreground">{products.length} product{products.length === 1 ? "" : "s"}</p>
        <Button size="sm" data-testid="add-product-btn" onClick={openNew} className="gap-1.5">
          <Plus className="h-4 w-4" /> Add product
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <Package className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">No products yet. Add your first item.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((p) => (
            <div key={p.id} className="rounded-lg border border-border/70 p-4" data-testid={`product-row-${p.id}`}>
              <div className="flex items-start gap-3">
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.name} className="h-14 w-14 rounded-md object-cover border border-border" />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-md border border-border bg-secondary"><Package className="h-5 w-5 text-muted-foreground" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{p.name}</span>
                    {!p.active && <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Hidden</span>}
                    {p.category && <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{p.category}</span>}
                  </div>
                  <div className="text-[11px] font-mono text-muted-foreground mt-0.5">Total stock: {p.total_stock}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`delete-product-${p.id}`} onClick={() => remove(p)} className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {p.variants.map((v) => (
                  <div key={v.id} className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-1.5" data-testid={`variant-row-${v.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-medium truncate">{variantLabel(v)}</span>
                      {v.sku && <span className="text-[10px] font-mono text-muted-foreground">{v.sku}</span>}
                      <span className="text-[11px] text-muted-foreground">${Number(v.price).toFixed(2)}</span>
                      {v.low_stock && <span className="inline-flex items-center gap-1 text-[10px] text-amber-400"><AlertTriangle className="h-3 w-3" />low</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {v.track_inventory ? (
                        <>
                          <Button variant="ghost" size="icon" data-testid={`stock-minus-${v.id}`} onClick={() => quickAdjust(p, v, -1)} className="h-6 w-6"><Minus className="h-3 w-3" /></Button>
                          <span data-testid={`stock-value-${v.id}`} className={`w-8 text-center text-xs font-mono ${v.stock === 0 ? "text-destructive" : ""}`}>{v.stock}</span>
                          <Button variant="ghost" size="icon" data-testid={`stock-plus-${v.id}`} onClick={() => quickAdjust(p, v, 1)} className="h-6 w-6"><PlusCircle className="h-3 w-3" /></Button>
                        </>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">Unlimited</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Edit product" : "New product"}</DialogTitle>
            <DialogDescription>Products can have one or more variants (e.g. sizes/colors) with their own price and stock.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Classic Tee" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Category / type</Label>
                <Input data-testid="product-category-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder="Apparel, Food, Electronics…" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea data-testid="product-description-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Image URLs (one per line)</Label>
              <Textarea data-testid="product-images-input" value={form.images.join("\n")} onChange={(e) => setForm((f) => ({ ...f, images: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) }))} rows={2} placeholder="https://…/photo1.jpg" className="font-mono text-xs" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Front image URL</Label>
                <Input data-testid="product-image-input" value={form.image} onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))} placeholder="https://…/front.png" className="font-mono text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Back image URL</Label>
                <Input data-testid="product-imageback-input" value={form.imageBack} onChange={(e) => setForm((f) => ({ ...f, imageBack: e.target.value }))} placeholder="https://…/back.png" className="font-mono text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Model image URL</Label>
                <Input data-testid="product-modelimage-input" value={form.modelImage} onChange={(e) => setForm((f) => ({ ...f, modelImage: e.target.value }))} placeholder="https://…/model.jpg" className="font-mono text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Model image (back) URL</Label>
                <Input data-testid="product-modelimageback-input" value={form.modelImageBack} onChange={(e) => setForm((f) => ({ ...f, modelImageBack: e.target.value }))} placeholder="https://…/model-back.jpg" className="font-mono text-xs" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Occasions (comma separated)</Label>
              <Input data-testid="product-occasions-input" value={form.occasions.join(", ")} onChange={(e) => setForm((f) => ({ ...f, occasions: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))} placeholder="e.g. date-night, casual" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <span className="text-sm font-medium">New</span>
                <Switch data-testid="product-isnew-switch" checked={form.isNew} onCheckedChange={(v) => setForm((f) => ({ ...f, isNew: v }))} />
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <span className="text-sm font-medium">On sale</span>
                <Switch data-testid="product-onsale-switch" checked={form.onSale} onCheckedChange={(v) => setForm((f) => ({ ...f, onSale: v }))} />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-medium">Active (visible for sale)</div>
                <div className="text-xs text-muted-foreground">Hidden products aren't returned to your storefront.</div>
              </div>
              <Switch data-testid="product-active-switch" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Option types (comma separated, optional)</Label>
              <Input data-testid="product-optiontypes-input" value={form.option_types.join(", ")} onChange={(e) => setOptionTypes(e.target.value)} placeholder="e.g. Size, Color" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Variants</Label>
                <Button variant="outline" size="sm" data-testid="add-variant-btn" onClick={addVariant} className="h-7 gap-1 text-[11px]"><Plus className="h-3 w-3" /> Variant</Button>
              </div>
              {form.variants.map((v, i) => (
                <div key={i} className="rounded-md border border-border/70 p-3 space-y-2" data-testid={`variant-editor-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-mono text-muted-foreground">Variant {i + 1}</span>
                    {form.variants.length > 1 && (
                      <Button variant="ghost" size="icon" data-testid={`remove-variant-${i}`} onClick={() => removeVariant(i)} className="h-6 w-6 text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                  {form.option_types.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      {form.option_types.map((t) => (
                        <div key={t} className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground">{t}</Label>
                          <Input data-testid={`variant-${i}-option-${t}`} value={v.options[t] || ""} onChange={(e) => setVariantOption(i, t, e.target.value)} placeholder={t} className="h-8 text-xs" />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">SKU (optional)</Label>
                      <Input data-testid={`variant-${i}-sku`} value={v.sku} onChange={(e) => updateVariant(i, { sku: e.target.value })} className="h-8 text-xs font-mono" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Price</Label>
                      <Input data-testid={`variant-${i}-price`} type="number" step="0.01" value={v.price} onChange={(e) => updateVariant(i, { price: e.target.value })} className="h-8 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Stock</Label>
                      <Input data-testid={`variant-${i}-stock`} type="number" value={v.stock} onChange={(e) => updateVariant(i, { stock: e.target.value })} disabled={!v.track_inventory} className="h-8 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Low-stock ≤</Label>
                      <Input data-testid={`variant-${i}-threshold`} type="number" value={v.low_stock_threshold} onChange={(e) => updateVariant(i, { low_stock_threshold: e.target.value })} placeholder="—" className="h-8 text-xs" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Variant image URLs (one per line, optional)</Label>
                    <Textarea data-testid={`variant-${i}-images`} value={(v.images || []).join("\n")} onChange={(e) => updateVariant(i, { images: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={1} className="font-mono text-[11px]" />
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[11px] text-muted-foreground">Track inventory for this variant</span>
                    <Switch data-testid={`variant-${i}-track`} checked={v.track_inventory} onCheckedChange={(val) => updateVariant(i, { track_inventory: val })} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button data-testid="save-product-btn" onClick={save} disabled={busy || !form.name}>{busy ? "Saving…" : editingId ? "Save changes" : "Create product"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
